import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "./../services/axiosInstance";
import { User } from "../types/User";

type SortField = keyof User;
type SortDirection = "asc" | "desc";

const fetchUsersApi = async (): Promise<User[]> => {
  const res = await axios.get<User[]>("/api/users");
  return res.data.map((user) => ({ ...user, createdAt: new Date(user.createdAt) }));
};

export const useUsers = () => {
  const queryClient = useQueryClient();

  const [searchTerm,     setSearchTerm]     = useState("");
  const [sortField,      setSortField]      = useState<SortField>("nom");
  const [sortDirection,  setSortDirection]  = useState<SortDirection>("asc");
  const [currentPage,    setCurrentPage]    = useState(1);
  const [itemsPerPage]                      = useState(5);

  const {
    data: allUsers = [],
    isLoading,
    isError,
    error: queryError,
  } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn:  fetchUsersApi,
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

 

  const updateMutation = useMutation({
    mutationFn: (userData: User) => {
      const { id, ...rest } = userData;
      return axios.put(`/api/users/${id}`, rest);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string | number) =>
      axios.delete(`/api/users/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const loading = isLoading
     || updateMutation.isPending
    || deleteMutation.isPending;

  const error =
    (isError          ? (queryError  as any)?.response?.data?.error || "Erreur lors du chargement"   : null) ||
     (updateMutation.isError ? (updateMutation.error as any)?.response?.data?.error || "Erreur mise à jour" : null) ||
    (deleteMutation.isError ? (deleteMutation.error as any)?.response?.data?.error || "Erreur suppression" : null);

  const filteredAndSortedUsers = useMemo(() => {
    const filtered = allUsers.filter((user) =>
      !searchTerm ||
      user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal instanceof Date && bVal instanceof Date)
        return sortDirection === "asc"
          ? aVal.getTime() - bVal.getTime()
          : bVal.getTime() - aVal.getTime();
      if (typeof aVal === "string" && typeof bVal === "string")
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return 0;
    });

    return filtered;
  }, [allUsers, searchTerm, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(start, start + itemsPerPage);
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const clearError   = () => {
     updateMutation.reset();
    deleteMutation.reset();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  return {
    users:      paginatedUsers,
    allUsers,
    loading,
    error,
    searchTerm,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    totalUsers: filteredAndSortedUsers.length,
    itemsPerPage,
    setSearchTerm,
    setCurrentPage,
    handleSort,
    updateUser: updateMutation.mutateAsync,
    deleteUser: deleteMutation.mutateAsync,
    clearError,
    clearFilters,
  };
};